import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { bp } from 'Styles/helpers';
import Helmet from 'react-helmet';
import objectPath from 'object-path';
import screen from 'superior-mq';
import apiData from 'Lib/apiData';
import cmsData from 'Lib/cmsData';
import createSlug from 'Lib/createSlug';
import formatMoney from 'Lib/formatMoney';
import FourOhFour from '../../components/four-oh-four';
import UnstyledList from '../../components/unstyled-list';
import DetailHero from '../../components/details/detail-hero';
import DetailStatus from '../../components/details/detail-status';
import DetailOverview from '../../components/details/detail-overview';
import DetailStart from '../../components/details/detail-start';
import InlineLink from '../../components/inline-link';

const listStyles = `
  a {
    text-decoration: none;
  }
`;

export default class FundraiserDetail extends PureComponent {

  static async getInitialProps({ query }) {
    const { id } = query;

    const contentParams = {
      content_type: 'projectDetailsImageGrid',
      'fields.projectType': 'fundraiser',
    };

    const [fundraiser, imageGroup] = await Promise.all([
      apiData(`/v1/fundraisers/${id}`),
      cmsData(contentParams),
    ]);

    return { fundraiser, id, imageGroup: imageGroup && imageGroup[0] };
  }

  formatStats = (stats) => {
    const rawFunds = (stats.fundsRaised && stats.fundsRaised.raw) || 0;
    const rawGoal = (stats.fields.goal && stats.fields.goal.raw) || 0;
    const percentFunded = Math.round((rawFunds / rawGoal) * 100);

    return [
      {
        value: (stats.fundsRaised && stats.fundsRaised.formatted) || '$0',
        title: `Funds Raised (${stats.billing && stats.billing.settlementCurrency})`,
      },
      {
        value: rawGoal - rawFunds < 0 ? '$0' : formatMoney(rawGoal - rawFunds),
        title: 'Remaining to Goal',
      },
      {
        value: percentFunded >= 100 ? '100%' : `${percentFunded}%`,
        title: 'Percent Funded',
      },
    ];
  };

  render() {
    const { fundraiser, imageGroup } = this.props;
    const todayDate = new Date();

    if (fundraiser.success && fundraiser.message) return <FourOhFour statusCode={404} />;

    const parentSlug = fundraiser.parentProject && fundraiser.parentProject.title
      ? `/programs/${fundraiser.parentProject.id}-${createSlug(fundraiser.parentProject.title)}`
      : '/programs';
    const metaDescription = fundraiser.fields.description.substring(0, 199);
    const featuredImage = objectPath.get(fundraiser, 'fields.featuredImage.photo.file.url');
    const ogImage = {
      fb: featuredImage ? `https:${featuredImage}?w=1200&h=630&fit=thumb` : `${process.env.S3_ROOT}/w4w-static/images/w4w-fb.png`,
      twitter: featuredImage ? `https:${featuredImage}?w=1200&h=600&fit=thumb` : `${process.env.S3_ROOT}/w4w-static/images/w4w-tw.png`,
    };

    return (
      <main>
        <Helmet>
          <title>{fundraiser.title}</title>
          <meta property="og:title" content={fundraiser.title} />
          <meta name="twitter:title" content={fundraiser.title} />
          {ogImage && <meta property="og:image" content={ogImage.fb} />}
          {ogImage && <meta name="twitter:image" content={ogImage.twitter} />}
          <meta name="description" content={metaDescription} />
          <meta property="og:description" content={metaDescription} />
          <meta name="twitter:description" content={metaDescription} />
        </Helmet>

        <DetailHero
          title="Support a Fundraiser"
          subheading={fundraiser.title}
          image={fundraiser.fields.featuredImage}
        >
          <UnstyledList styles={listStyles}>

            {fundraiser.parentProject.id > 0 &&
              <li>
                {/* eslint-disable-next-line max-len */}
                All Funds Support:<Break /> <InlineLink href={parentSlug}>{fundraiser.parentProject.title}</InlineLink>
              </li>
            }

            {fundraiser.creator && (fundraiser.creator.firstName || fundraiser.creator.lastName) &&
              <li>
                Organizer: <InlineLink href={`/users/${fundraiser.creator.id}`}>{fundraiser.creator.firstName} {fundraiser.creator.lastName}</InlineLink>
              </li>
            }

          </UnstyledList>
        </DetailHero>

        <DetailStatus
          title="Fundraiser Status"
          stats={this.formatStats(fundraiser)}
          id={this.props.id}
          inputIsDark
          showDonation={fundraiser.status === 'published' && (!fundraiser.endDate || new Date(fundraiser.endDate) > todayDate)}
        />

        <DetailOverview
          sectionTitle="Fundraiser Overview"
          projectTitle={fundraiser.title}
          content={fundraiser.fields.description}
          slug={fundraiser.slug}
        />

        <DetailStart
          content={imageGroup.fields}
          waveColor="#69a4b8"
        />
      </main>
    );
  }
}

const Break = styled.br`
  ${screen.above(bp.mobile, `
    display: none;
  `)}
`;
